// #tag: ROOT CLIENT APPLICATION ROUTER
// #purpose: Defines declarative React Router routes, authentication guards, and layout shells for student and admin views.
import React, { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { RequireAuthLayout } from './components/auth/RequireAuth';
import { RequireAdminLayout } from './components/auth/RequireAdmin';
import { RedirectStaffFromStudentSite } from './components/auth/RedirectAdminsFromStudent';
import { UiSoundListener } from './components/sound/UiSoundListener';
import { AppShell } from './components/layout/AppShell';
import { AdminShell } from './components/admin/AdminShell';
import { ErrorPage } from './components/ErrorPage';
import { LoadingState } from './components/states/StateViews';
import { LoginScreen } from './screens/LoginScreen';
import { RegisterScreen } from './screens/RegisterScreen';
import { HomeScreen } from './screens/HomeScreen';
import { ResultsScreen } from './screens/ResultsScreen';
import { ResultDetailScreen } from './screens/ResultDetailScreen';
import { OpportunitiesScreen } from './screens/OpportunitiesScreen';
import { OpportunityDetailScreen } from './screens/OpportunityDetailScreen';
import { AdmitCardsScreen } from './screens/AdmitCardsScreen';
import { StudentVaultScreen } from './screens/StudentVaultScreen';
import { DashboardScreen } from './screens/DashboardScreen';
import { NotificationsScreen } from './screens/NotificationsScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { AdminDashboardScreen } from './screens/admin/AdminDashboardScreen';
import { AdminUsersScreen } from './screens/admin/AdminUsersScreen';
import { AdminRolesScreen, AdminPermissionsScreen } from './screens/admin/AdminAccessScreens';
import { AdminBannersScreen } from './screens/admin/AdminBannersScreen';
import { AdminPagesScreen } from './screens/admin/AdminPagesScreen';
import { AdminBlogsScreen } from './screens/admin/AdminBlogsScreen';
import { AdminFaqsScreen } from './screens/admin/AdminFaqsScreen';
import { AdminAnnouncementsScreen } from './screens/admin/AdminAnnouncementsScreen';
import { AdminNavigationScreen } from './screens/admin/AdminNavigationScreen';
import { AdminResultsScreen } from './screens/admin/AdminResultsScreen';
import { AdminOpportunitiesScreen } from './screens/admin/AdminOpportunitiesScreen';
import { AdminJobsScreen } from './screens/admin/AdminJobsScreen';
import { AdminAcademyScreen } from './screens/admin/AdminAcademyScreen';
import { AdminCollegesScreen } from './screens/admin/AdminCollegesScreen';
import { AdminMediaScreen } from './screens/admin/AdminMediaScreen';
import { AdminNotificationsScreen } from './screens/admin/AdminNotificationsScreen';
import { AdminSeoScreen } from './screens/admin/AdminSeoScreen';
import { AdminAnalyticsScreen } from './screens/admin/AdminAnalyticsScreen';
import { AdminSupportScreen } from './screens/admin/AdminSupportScreen';
import { AdminAuditScreen } from './screens/admin/AdminAuditScreen';
import { AdminAiScreen } from './screens/admin/AdminAiScreen';
import { AdminSettingsScreen } from './screens/admin/AdminSettingsScreen';
import { AdminProfileScreen } from './screens/admin/AdminProfileScreen';
import './styles/global.css';
import './styles/results.css';
import './styles/admin.css';

// #tag: ROOT APPLICATION COMPONENT
// #purpose: Assembles top-level error boundaries, sound feedback listeners, and route branching.
function App() {
  return (
    <Suspense fallback={<LoadingState label="Loading WebFoxx Results…" />}>
      <UiSoundListener />
      <Routes>
        <Route element={<RequireAdminLayout />}>
          <Route path="/admin" element={<AdminShell />}>
            <Route index element={<AdminDashboardScreen />} />
            <Route path="users" element={<AdminUsersScreen />} />
            <Route path="roles" element={<AdminRolesScreen />} />
            <Route path="permissions" element={<AdminPermissionsScreen />} />
            <Route path="content/pages" element={<AdminPagesScreen />} />
            <Route path="content/blogs" element={<AdminBlogsScreen />} />
            <Route path="content/faqs" element={<AdminFaqsScreen />} />
            <Route path="content/announcements" element={<AdminAnnouncementsScreen />} />
            <Route path="content/navigation" element={<AdminNavigationScreen />} />
            <Route path="content/banners" element={<AdminBannersScreen />} />
            <Route path="results" element={<AdminResultsScreen />} />
            <Route path="opportunities" element={<AdminOpportunitiesScreen />} />
            <Route path="academy" element={<AdminAcademyScreen />} />
            <Route path="jobs" element={<AdminJobsScreen />} />
            <Route path="colleges" element={<AdminCollegesScreen />} />
            <Route path="media" element={<AdminMediaScreen />} />
            <Route path="notifications" element={<AdminNotificationsScreen />} />
            <Route path="seo" element={<AdminSeoScreen />} />
            <Route path="analytics" element={<AdminAnalyticsScreen />} />
            <Route path="support" element={<AdminSupportScreen />} />
            <Route path="audit" element={<AdminAuditScreen />} />
            <Route path="ai" element={<AdminAiScreen />} />
            <Route path="settings" element={<AdminSettingsScreen />} />
            <Route path="profile" element={<AdminProfileScreen />} />
          </Route>
        </Route>

        <Route element={<RedirectStaffFromStudentSite />}>
          <Route element={<AppShell />}>
            <Route path="login" element={<LoginScreen />} />
            <Route path="register" element={<RegisterScreen />} />
            <Route index element={<HomeScreen />} />
            <Route path="results" element={<ResultsScreen />} />
            <Route path="results/:id" element={<ResultDetailScreen />} />
            <Route path="opportunities" element={<OpportunitiesScreen />} />
            <Route path="opportunities/:slug" element={<OpportunityDetailScreen />} />
            <Route path="admit-cards" element={<AdmitCardsScreen />} />
            <Route path="settings" element={<SettingsScreen />} />
            <Route element={<RequireAuthLayout />}>
              <Route path="dashboard" element={<DashboardScreen />} />
              <Route path="vault" element={<StudentVaultScreen />} />
              <Route path="notifications" element={<NotificationsScreen />} />
            </Route>
            <Route path="*" element={<ErrorPage code={404} />} />
          </Route>
        </Route>
        <Route path="home" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
